import type { CSSProperties } from 'react';
import { alpha, createTheme, responsiveFontSizes } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    sectionTitle: CSSProperties;
    fieldLabel: CSSProperties;
    bodyLg: CSSProperties;
    bodyMd: CSSProperties;
  }

  interface TypographyVariantsOptions {
    sectionTitle?: CSSProperties;
    fieldLabel?: CSSProperties;
    bodyLg?: CSSProperties;
    bodyMd?: CSSProperties;
  }

  interface Palette {
    ds: {
      primary: string;
      primaryHover: string;
      primaryActive: string;
      secondary: string;
      secondaryHover: string;
      secondaryActive: string;
      disabledBg: string;
      disabledText: string;
      heading: string;
      body: string;
      surfaceBlue: string;
      surfaceBlueSoft: string;
      surfaceTeal: string;
      surfaceRose: string;
      surfacePeach: string;
      surfaceIce: string;
      neutral: {
        900: string;
        300: string;
        200: string;
        100: string;
      };
      system: {
        success: string;
        warning: string;
        error: string;
        info: string;
      };
    };
  }

  interface PaletteOptions {
    ds?: Palette['ds'];
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    sectionTitle: true;
    fieldLabel: true;
    bodyLg: true;
    bodyMd: true;
  }
}

const ds = {
  primary: '#20AEDC',
  primaryHover: '#2288A8',
  primaryActive: '#5AB7D5',
  secondary: '#2EC3B6',
  secondaryHover: '#249A8F',
  secondaryActive: '#74D8CF',
  disabledBg: '#CFCFD1',
  disabledText: '#F4F4F4',
  heading: '#1D2B2D',
  body: '#1D2B2D',
  surfaceBlue: '#DCF7FF',
  surfaceBlueSoft: '#E8FBFF',
  surfaceTeal: '#BFFFFF',
  surfaceRose: '#FFE8EC',
  surfacePeach: '#FFEEDF',
  surfaceIce: '#F7FFFF',
  neutral: {
    900: '#1D2B2D',
    300: '#E1E1E1',
    200: '#F3F5F7',
    100: '#FFFFFF',
  },
  system: {
    success: '#1CE189',
    warning: '#FFA606',
    error: '#FF6262',
    info: '#3091FF',
  },
} as const;

const fontPrimary = 'var(--font-family-primary), Inter, sans-serif';
const fontHeading = 'var(--font-family-heading), Inter, sans-serif';
const fontBody = 'var(--font-family-body), Manrope, sans-serif';

const baseTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: ds.primary,
      dark: ds.primaryHover,
      light: ds.primaryActive,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: ds.secondary,
      dark: ds.secondaryHover,
      light: ds.secondaryActive,
      contrastText: '#FFFFFF',
    },
    success: {
      main: ds.system.success,
    },
    warning: {
      main: ds.system.warning,
    },
    error: {
      main: ds.system.error,
    },
    info: {
      main: ds.system.info,
    },
    background: {
      default: '#FAF9F6',
      paper: ds.neutral[100],
    },
    text: {
      primary: ds.heading,
      secondary: alpha(ds.body, 0.7),
    },
    divider: ds.neutral[300],
    ds,
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: fontBody,
    h1: {
      fontFamily: fontHeading,
      fontSize: '3.5rem',
      lineHeight: 1.1,
      fontWeight: 600,
      color: ds.heading,
    },
    h2: {
      fontFamily: fontHeading,
      fontSize: '3rem',
      lineHeight: 1.1,
      fontWeight: 600,
      color: ds.heading,
    },
    h3: {
      fontFamily: fontHeading,
      fontSize: '2.5rem',
      lineHeight: 1.1,
      fontWeight: 600,
      color: ds.heading,
    },
    h4: {
      fontFamily: fontHeading,
      fontSize: '2rem',
      lineHeight: 1.1,
      fontWeight: 600,
      color: ds.heading,
    },
    h5: {
      fontFamily: fontHeading,
      fontSize: '1.5rem',
      lineHeight: 1.1,
      fontWeight: 600,
      color: ds.heading,
    },
    h6: {
      fontFamily: fontHeading,
      fontSize: '1.25rem',
      lineHeight: 1.1,
      fontWeight: 600,
      color: ds.heading,
    },
    bodyLg: {
      fontFamily: fontBody,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      fontWeight: 400,
      color: ds.body,
    },
    bodyMd: {
      fontFamily: fontBody,
      fontSize: '1.125rem',
      lineHeight: 1.4,
      fontWeight: 400,
      color: ds.body,
    },
    body1: {
      fontFamily: fontBody,
      fontSize: '1rem',
      lineHeight: 1.4,
      fontWeight: 400,
      color: ds.body,
    },
    body2: {
      fontFamily: fontBody,
      fontSize: '0.875rem',
      lineHeight: 1.4,
      fontWeight: 400,
      color: ds.body,
    },
    sectionTitle: {
      fontFamily: fontHeading,
      fontSize: '1rem',
      lineHeight: 1.4,
      fontWeight: 600,
      color: ds.heading,
    },
    fieldLabel: {
      fontFamily: fontBody,
      fontSize: '0.75rem',
      lineHeight: 1,
      letterSpacing: '0.15px',
      fontWeight: 400,
      color: alpha(ds.body, 0.7),
    },
    button: {
      fontFamily: fontPrimary,
      fontSize: '1rem',
      lineHeight: 1.4,
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: 0,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: fontBody,
          color: ds.body,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${alpha(ds.neutral[900], 0.08)}`,
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
          backgroundColor: ds.neutral[100],
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
          '&.Mui-disabled': {
            backgroundColor: ds.disabledBg,
            color: ds.disabledText,
            borderColor: ds.disabledBg,
          },
        },
        containedPrimary: {
          backgroundColor: ds.primary,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: ds.primaryHover,
          },
          '&:active': {
            backgroundColor: ds.primaryActive,
          },
        },
        containedSecondary: {
          backgroundColor: ds.secondary,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: ds.secondaryHover,
          },
          '&:active': {
            backgroundColor: ds.secondaryActive,
          },
        },
        outlinedPrimary: {
          borderColor: ds.primary,
          color: ds.primary,
          '&:hover': {
            borderColor: ds.primary,
            backgroundColor: ds.surfaceBlue,
          },
          '&:active': {
            backgroundColor: ds.surfaceBlueSoft,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: ds.neutral[100],
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: ds.neutral[300],
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: ds.primary,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: ds.primary,
            borderWidth: 1,
          },
        },
        input: {
          fontFamily: fontBody,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily: fontBody,
          fontSize: '0.75rem',
          // Tight line-height + padding:0 clips descenders (g, y, p); give glyphs vertical room.
          lineHeight: 1.4,
          paddingTop: '3px',
          paddingBottom: '3px',
          letterSpacing: '0.15px',
          color: alpha(ds.body, 0.7),
          '&.Mui-focused': {
            color: ds.primary,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 600,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: ds.primary,
          '&:hover': {
            color: ds.primaryHover,
          },
        },
      },
    },
  },
});

export const muiTheme = responsiveFontSizes(baseTheme);
